import { useState } from 'react';
import { useTranslation } from '@packages/utils';
import EditableInput from 'components/Base/EditableFields/EditableInput';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import { useEditEventCapacityMutation } from 'redux/apiSlice/events.slice';
import { TRtkErrorType } from 'data/types/generic.types';
import useGetValidationData from 'components/Base/Form/validationRules';
import { ECapacityEmptyTexts, EEventStatus } from 'data/types/events.types';
import { useTranslateCapacityShowValue } from 'pages/private/Events/hooks/useTranslateCapacityShowValue';
import { Flex } from 'antd';
import { CapacityOutline } from 'assets/icon/icon';
import { Text } from 'components/Base/Typography';
import { TEventCapacityProps } from './EventCapacity.types';

const EventCapacity = ({
  eventId,
  capacity,
  eventStatus,
}: TEventCapacityProps) => {
  const { allowedOperations, value, showValue } = capacity || {};
  const { EValidationMessages, validationRules } = useGetValidationData();
  const { t } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.events.details.basic-info.event-capacity',
  });
  const capacityText = useTranslateCapacityShowValue(
    showValue as ECapacityEmptyTexts,
  );
  const { showToastNotification } = useShowToastNotification();
  const [editEventCapacity, { isLoading, isError }] =
    useEditEventCapacityMutation();
  const [errorMessage, setErrorMessage] = useState('');

  const validateCapacity = async (newValue: string) => {
    const numericValue = Number(newValue);
    const isCleared = typeof newValue && !!newValue;
    if (
      eventStatus === EEventStatus.PUBLISHED &&
      Number(value) > numericValue &&
      isCleared
    ) {
      return Promise.reject(new Error(EValidationMessages.DECREASE_IMPOSSIBLE));
    }
    return Promise.resolve();
  };
  const onEdit = async (val: string) => {
    try {
      await validateCapacity(val);
      await editEventCapacity({
        eventId,
        value: val,
      }).unwrap();
      showToastNotification({
        message: t('SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
      setErrorMessage('');
    } catch (e) {
      setErrorMessage(
        (e as TRtkErrorType)?.data?.message || (e as Error).message,
      );
      showToastNotification({
        message: (e as TRtkErrorType)?.data?.message || (e as Error).message,
        type: ENotificationType.ERROR,
      });
    }
  };

  return (
    <Flex align="center" data-cy="event-capacity-input" gap={2}>
      <Flex align="center" gap={8} style={{ width: 160 }}>
        <CapacityOutline size={24} />
        <Text color="#808080">Capacity</Text>
      </Flex>
      <EditableInput
        onEdit={onEdit}
        title={value}
        fitContent
        showValue={capacityText}
        isError={isError}
        errorMessage={errorMessage}
        isLoading={isLoading}
        readonly={
          allowedOperations?.readonly ||
          (showValue === ECapacityEmptyTexts.UNLIMITED_LONG &&
            eventStatus === EEventStatus.PUBLISHED)
        }
        canRemove={allowedOperations?.canRemove}
        rules={[validationRules.positiveNumbers()]}
        backIcon
      />
    </Flex>
  );
};

export default EventCapacity;
